/**
 * Mixin de exportación a Excel para el módulo "Registros de Acceso".
 *
 * Replica la técnica de gymsales: descarga ciclo a ciclo del endpoint de reporte
 * (`GetGymAccessReportFormat`) en bloques grandes y concurrentes, y vuelca el
 * arreglo plano resultante a una plantilla Excel (`RegistroAccesosGimnasio`)
 * mediante xlsx-populate. A diferencia de gymsales, los registros de acceso son
 * planos (sin sub-arreglos), por lo que se usa una sola hoja con una fila por
 * registro.
 *
 * Ver "Documentación Proyecto/Log de Accesos/ENDPOINT-LOG-ACCESO-DASHBOARD.md"
 * (sección 3.5).
 */

import XLSXpopulate from 'xlsx-populate';

const OP_REPORT = '/v1/access/get/gymaccess/reportformat';
const TEMPLATE_NAME = 'RegistroAccesosGimnasio';
const EXPORT_LIMIT = 500;        // limit alto permitido por el backend (1..1000)
const CONCURRENCY = 3;           // páginas en paralelo por lote
const DATA_START_ROW = 7;        // primera fila de datos en la plantilla
const FILL_CHUNK = 200;          // filas por bloque al volcar (no bloquear la UI)

export default {
  data() {
    return {
      exporting: false
    };
  },

  methods: {
    /**
     * Punto de entrada: descarga todo el universo filtrado y genera el Excel.
     * @param {Object} parameters - Filtros unificados (sección 2 del contrato),
     *   sin page/limit/sort (se añaden internamente).
     */
    async exportAccesosToExcel(parameters) {
      if (this.exporting) return;
      this.exporting = true;
      try {
        const data = await this.downloadAccesosForExport(parameters || {});

        if (!data.length) {
          this.$swal.fire({
            icon: 'info',
            title: 'Sin registros',
            text: 'No hay accesos para exportar con los filtros seleccionados.',
            confirmButtonColor: '#6bd098'
          });
          return;
        }

        const template = await this.fetchAccesosTemplate();
        await this.generateAccesosExcel(data, template);
      } catch (error) {
        this.$logger.error('Error en exportación de accesos:', error);
        const status = error && error.response && error.response.status;
        if (status === 401) return; // gestionado por el interceptor de axios
        this.$swal.fire({
          icon: 'error',
          title: 'Error al exportar',
          html: `
            <p>${(error && error.message) || 'Ocurrió un error durante la exportación.'}</p>
            <p class="text-muted small mt-2">
              Si el problema persiste con grandes volúmenes, intenta exportar un rango de fechas más acotado.
            </p>
          `,
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.exporting = false;
      }
    },

    /**
     * Descarga ciclo a ciclo todas las páginas del reporte en lotes concurrentes.
     * Termina cuando una página devuelve menos de EXPORT_LIMIT registros.
     */
    async downloadAccesosForExport(parameters) {
      const baseParams = {
        ...parameters,
        limit: EXPORT_LIMIT,
        sortField: 'fechaHoraIngreso',
        sortDirection: 'desc'
      };

      this.$swal.fire({
        title: 'Descargando registros de acceso…',
        html: `
          <div class="text-center">
            <div class="mb-3"><i class="fas fa-download fa-3x text-primary"></i></div>
            <div class="progress mb-3" style="height: 25px;">
              <div id="acc-download-bar" class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                   role="progressbar" style="width: 0%">0 registros</div>
            </div>
            <p class="text-muted small"><span id="acc-download-status">Iniciando descarga…</span></p>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => { this.$swal.showLoading(); }
      });

      const fetchPage = (page) => this.$CentralGymOPS_API.post(
        '',
        {
          Header: { Operacion: OP_REPORT },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters: { ...baseParams, page },
          Body: {}
        },
        { headers: { 'Content-Type': 'application/json' } }
      ).then((response) => ({
        page,
        data: (response.data && response.data.result) || []
      }));

      const data = [];
      let page = 0;
      let keepFetching = true;

      while (keepFetching) {
        const pages = [];
        for (let i = 0; i < CONCURRENCY; i++) pages.push(page + i);

        const results = await Promise.all(pages.map(fetchPage));
        results.sort((a, b) => a.page - b.page);

        // Acumular en orden; detenerse en la primera página corta (o vacía).
        for (const r of results) {
          data.push(...r.data);
          if (r.data.length < EXPORT_LIMIT) {
            keepFetching = false;
            break;
          }
        }

        this.updateDownloadProgress(data.length);
        page += CONCURRENCY;
      }

      return data;
    },

    updateDownloadProgress(count) {
      const bar = document.getElementById('acc-download-bar');
      const status = document.getElementById('acc-download-status');
      if (bar) {
        bar.textContent = `${count.toLocaleString('es-CL')} registros`;
        bar.style.width = Math.min((count / 1000) * 100, 90) + '%';
      }
      if (status) status.textContent = `Descargados ${count.toLocaleString('es-CL')} registros…`;
    },

    /**
     * Obtiene la plantilla Excel desde el servicio de plantillas.
     */
    async fetchAccesosTemplate() {
      const response = await this.$XLSXTemplateDownload.post(
        '',
        { template: TEMPLATE_NAME },
        {
          headers: {
            'Content-Type': 'application/json',
            token: sessionStorage.getItem('jCxbraoQeD')
          },
          responseType: 'arraybuffer'
        }
      );
      return response.data;
    },

    /**
     * Vuelca los registros a la plantilla y dispara la descarga del archivo.
     */
    async generateAccesosExcel(data, template) {
      this.$swal.fire({
        title: 'Generando archivo Excel…',
        html: `
          <div class="text-center">
            <div class="mb-3"><i class="fas fa-file-excel fa-3x text-success"></i></div>
            <div class="progress mb-3" style="height: 25px;">
              <div id="acc-export-bar" class="progress-bar progress-bar-striped progress-bar-animated bg-success"
                   role="progressbar" style="width: 0%">0%</div>
            </div>
            <p class="text-muted small">
              Procesando <strong>${data.length.toLocaleString('es-CL')}</strong> registros
            </p>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => { this.$swal.showLoading(); }
      });

      const wb = await XLSXpopulate.fromDataAsync(template);
      const sheet = wb.sheet(0);

      // Fecha de generación (convención gymsales: celda D3).
      const fechaGeneracion = this.$moment().format('DD/MM/YYYY - HH:mm:ss');
      if (sheet.cell('D3')) sheet.cell('D3').value(fechaGeneracion);

      // Volcado por bloques para no bloquear el hilo principal.
      for (let start = 0; start < data.length; start += FILL_CHUNK) {
        const end = Math.min(start + FILL_CHUNK, data.length);
        for (let i = start; i < end; i++) {
          this.fillAccesoRow(sheet, data[i], DATA_START_ROW + i, i + 1);
        }
        this.updateExportProgress(Math.round((end / data.length) * 90));
        // Ceder el hilo entre bloques.
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      this.updateExportProgress(95);
      const blob = await wb.outputAsync();
      this.updateExportProgress(100);

      const fileName = `Registro Accesos Gimnasio - ${this.$moment().format('DD-MM-YYYY - HH-mm-ss')}.xlsx`;
      this.triggerDownload(blob, fileName);

      this.$swal.fire({
        icon: 'success',
        title: '¡Exportación exitosa!',
        html: `
          <div class="text-center">
            <i class="fas fa-check-circle fa-3x text-success mb-3"></i>
            <p>Se exportaron <strong>${data.length.toLocaleString('es-CL')}</strong> registros de acceso.</p>
          </div>
        `,
        confirmButtonColor: '#6bd098',
        timer: 4000,
        timerProgressBar: true
      });
    },

    /**
     * Escribe un registro de acceso en una fila de la hoja.
     * El bloque de datos comienza en la columna C (C7), por lo que el mapeo
     * va de C a T.
     */
    fillAccesoRow(sheet, r, row, index) {
      sheet.cell(`C${row}`).value(index);
      sheet.cell(`D${row}`).value(r.rutSocio || '');
      sheet.cell(`E${row}`).value(r.nombrePersona || '');
      sheet.cell(`F${row}`).value(r.perfilPersona || '');
      sheet.cell(`G${row}`).value(r.estadoAcceso || '');
      sheet.cell(`H${row}`).value(r.fechaIngreso || '');
      sheet.cell(`I${row}`).value(r.horaIngreso || '');
      sheet.cell(`J${row}`).value(r.fechaHoraIngreso || '');
      sheet.cell(`K${row}`).value(r.metodoAcceso || '');
      sheet.cell(`L${row}`).value(r.detalleMetodoAcceso || '');
      sheet.cell(`M${row}`).value(r.idAcceso || '');
      sheet.cell(`N${row}`).value(r.nombreSucursalAcceso || '');
      sheet.cell(`O${row}`).value(r.nombreEmpresa || '');
      sheet.cell(`P${row}`).value(r.planAcceso || '');
      // Valor del plan como número cuando exista (para que Excel lo trate numérico).
      const valor = Number(r.valorPlan);
      sheet.cell(`Q${row}`).value(isNaN(valor) ? (r.valorPlan || '') : valor);
      sheet.cell(`R${row}`).value(r.sesionUtilizada != null ? r.sesionUtilizada : '');
      sheet.cell(`S${row}`).value(r.sesionesTotales != null ? r.sesionesTotales : '');
      sheet.cell(`T${row}`).value(r.detalleOpcional || '');
    },

    updateExportProgress(percentage) {
      const bar = document.getElementById('acc-export-bar');
      if (bar) {
        bar.style.width = `${percentage}%`;
        bar.textContent = `${percentage}%`;
        bar.setAttribute('aria-valuenow', percentage);
      }
    },

    triggerDownload(blob, fileName) {
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, fileName);
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  }
};
